import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import type { FinanceSummary, PaginatedPayments } from '../services/types';
import { formatDate, formatMoney } from '../utils/format';
import { useAuth } from '../hooks/useAuth';

const STATUS_OPTIONS = ['all', 'pending', 'processed', 'failed', 'refunded'];

export function PaymentsPage() {
  const { user } = useAuth();
  const canWrite = user?.role === 'finance' || user?.role === 'superadmin';
  const [searchParams, setSearchParams] = useSearchParams();

  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [data, setData] = useState<PaginatedPayments | null>(null);
  const [loading, setLoading] = useState(true);

  const page = Number(searchParams.get('page') || '1');
  const q = searchParams.get('q') || '';
  const status = searchParams.get('status') || 'all';
  const tier = searchParams.get('tier') || '';
  const user_id = searchParams.get('user_id') || '';

  const [qInput, setQInput] = useState(q);
  const [userIdInput, setUserIdInput] = useState(user_id);

  const [grantUserId, setGrantUserId] = useState('');
  const [grantTier, setGrantTier] = useState('premium');
  const [grantDays, setGrantDays] = useState('30');
  const [grantMsg, setGrantMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [sumRes, listRes] = await Promise.all([
      api.getFinanceSummary(),
      api.listPayments({
        page,
        page_size: 20,
        q: q || undefined,
        status: status !== 'all' ? status : undefined,
        tier: tier || undefined,
        user_id: user_id && /^\d+$/.test(user_id) ? Number(user_id) : undefined,
      }),
    ]);
    if (sumRes.data) setSummary(sumRes.data);
    if (listRes.data) setData(listRes.data);
    setLoading(false);
  }, [page, q, status, tier, user_id]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (qInput) next.set('q', qInput);
    else next.delete('q');
    if (userIdInput) next.set('user_id', userIdInput);
    else next.delete('user_id');
    next.set('page', '1');
    setSearchParams(next);
  };

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  const submitGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    setGrantMsg(null);
    const uid = Number(grantUserId);
    const days = Number(grantDays);
    if (!uid || !days) return;
    const res = await api.grantSubscription({ user_id: uid, tier: grantTier, days });
    if (res.data) {
      setGrantMsg(
        `Выдано: tier=${res.data.subscription_tier}, до ${formatDate(res.data.subscription_until)}, ключи ${res.data.keys_extended}/${res.data.keys_total}`,
      );
      load();
    } else if (res.error) {
      setGrantMsg(res.error.message);
    }
  };

  const statusBadge = (s: string) => {
    const cls =
      s === 'processed'
        ? 'badge badge-success'
        : s === 'pending'
          ? 'badge badge-warning'
          : s === 'failed' || s === 'refunded'
            ? 'badge badge-muted'
            : 'badge';
    return <span className={cls}>{s}</span>;
  };

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Платежи и финансы</h1>

      {summary && (
        <div className="grid-stats" style={{ marginBottom: '1.5rem' }}>
          <div className="card">
            <div className="stat-label">Сегодня</div>
            <div className="stat-value">{formatMoney(summary.revenue_today)}</div>
          </div>
          <div className="card">
            <div className="stat-label">Месяц</div>
            <div className="stat-value">{formatMoney(summary.revenue_month)}</div>
          </div>
          <div className="card">
            <div className="stat-label">Год</div>
            <div className="stat-value">{formatMoney(summary.revenue_year)}</div>
          </div>
          <div className="card">
            <div className="stat-label">Pending (24ч)</div>
            <div className="stat-value">{summary.pending_last_24h}</div>
          </div>
          <div className="card">
            <div className="stat-label">Processed всего</div>
            <div className="stat-value">{summary.processed_count}</div>
          </div>
        </div>
      )}

      {canWrite && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Выдать подписку</h3>
          <form onSubmit={submitGrant} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              className="input"
              placeholder="User ID"
              value={grantUserId}
              onChange={(e) => setGrantUserId(e.target.value)}
              style={{ width: 120 }}
            />
            <select className="select" value={grantTier} onChange={(e) => setGrantTier(e.target.value)}>
              <option value="premium">Premium</option>
              <option value="default">Default</option>
            </select>
            <input
              className="input"
              placeholder="Дней"
              value={grantDays}
              onChange={(e) => setGrantDays(e.target.value)}
              style={{ width: 80 }}
            />
            <button type="submit" className="btn btn-primary">
              Выдать
            </button>
          </form>
          {grantMsg && (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              {grantMsg}
            </p>
          )}
        </div>
      )}

      <form onSubmit={applyFilters} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input
          className="input"
          placeholder="Payment ID или user ID"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <input
          className="input"
          placeholder="User ID"
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
          style={{ width: 120 }}
        />
        <select className="select" value={status} onChange={(e) => setFilter('status', e.target.value)}>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'Все статусы' : s}
            </option>
          ))}
        </select>
        <select className="select" value={tier} onChange={(e) => setFilter('tier', e.target.value)}>
          <option value="">Все tier</option>
          <option value="premium">Premium</option>
          <option value="default">Default</option>
        </select>
        <button type="submit" className="btn btn-primary">
          Найти
        </button>
      </form>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && data && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Сумма</th>
                  <th>Tier</th>
                  <th>Дней</th>
                  <th>Статус</th>
                  <th>Дата</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ color: 'var(--text-muted)' }}>
                      Платежи не найдены
                    </td>
                  </tr>
                )}
                {data.items.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/payments/${encodeURIComponent(p.id)}`}>
                        <code style={{ fontSize: '0.75rem' }}>{p.id.slice(0, 16)}…</code>
                      </Link>
                    </td>
                    <td>
                      <Link to={`/users/${p.user_id}`}>
                        {p.first_name || p.username || p.user_id}
                      </Link>
                    </td>
                    <td>{formatMoney(p.amount)}</td>
                    <td>{p.subscription_tier ?? '—'}</td>
                    <td>{p.days}</td>
                    <td>{statusBadge(p.status)}</td>
                    <td>{formatDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (
            <div className="pagination">
              <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ←
              </button>
              <span style={{ color: 'var(--text-muted)' }}>
                {page} / {data.pages} ({data.total})
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= data.pages}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
